/*
 *  -----------------------------------------------------------------------
 *    <copyright file="extra-data-fields-grid.tsx" company="Triumph Research Intelligence">
 *        Copyright (C) 2019 by Triumph Research Intelligence. All rights reserved.
 *    </copyright>
 *   -----------------------------------------------------------------------
 *
 */
import { orderBy } from '@progress/kendo-data-query';
import React, { useEffect, useState } from 'react';

import { GridColumnMenuActiveFilter } from '../../core/grid-filters';
import { useGridCells, useTranslation } from 'hooks';

import { extraDataFields_OpraQueries_extraDataFields_ExtraDataField } from '@queries/generated/central-monitoring.generated';
import { restructuredUpdatedCreatedBy } from '@utils-clean/update-users-array';
import ExpandableGrid from 'views/components/core/expandable/grid';

import './styles.scss';

const ExtraDataFieldsGrid = ({ items, actions }: any) => {
    const { t } = useTranslation();
    const { MaxLengthCell, DateCell, YesNoCell } = useGridCells();

    const [data, setData] = useState([]);

    useEffect(() => {
        let array = [...items].map((item: extraDataFields_OpraQueries_extraDataFields_ExtraDataField) => ({
            ...item,
            _fieldType: t(`admin:${item.fieldType}_FIELD_TYPE`),
            _table: t(`admin:${item.table}_TABLE`),
        }));
        const newData = restructuredUpdatedCreatedBy(array);
        array = orderBy(newData, [{ field: 'friendlyName', dir: 'asc' }]) ?? [];

        setData(array);
    }, [items]);

    return (
        <ExpandableGrid
            customKey="extra-data-fields-grid"
            id="extraDataFieldsGrid"
            key="extraDataFieldsGrid"
            items={data}
            rowActions={actions}
            groupable={false}
            showPager
            addScroll
            scrollable="scrollable"
            reorderable
            pageable={false}
            resizable
            options={{
                allowExcelExport: false,
                allowExpandCollapseAll: false,
                useFilterChips: false,
            }}
            detail={null}
            columns={[
                {
                    field: 'active',
                    label: t('admin:active'),
                    id: 'extra-data-field-active',
                    minWidth: 150,
                    lockedByDefault: true,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    field: 'friendlyName',
                    label: t('admin:displayName'),
                    id: 'extra-data-field-displayName',
                    minWidth: 180,
                    lockedByDefault: true,
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    field: '_fieldType',
                    id: 'extra-data-field-fieldType',
                    label: t('admin:fieldType'),
                    minWidth: 200,
                    cells: { data: MaxLengthCell(14) },
                },
                {
                    field: 'isCalculated',
                    label: t('admin:isCalculated'),
                    id: 'extra-data-field-isCalculated',
                    minWidth: 180,
                    cells: { data: YesNoCell },
                    filterUi: GridColumnMenuActiveFilter,
                },
                {
                    field: '_table',
                    id: 'extra-data-field-dataSourceName',
                    label: t('admin:dataSourceName'),
                    minWidth: 220,
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    field: 'sourceFieldName',
                    id: 'extra-data-field-sourceColumnName',
                    label: t('admin:sourceColumnName'),
                    minWidth: 240,
                    cells: { data: MaxLengthCell(50) },
                },
                {
                    label: t('ra:createdBy'),
                    field: '_createdByString',
                    id: 'extra-data-field-createdby',
                    minWidth: 180,
                },
                {
                    label: t('ra:createdDate'),
                    field: 'created',
                    id: 'extra-data-field-created',
                    filter: 'date',
                    minWidth: 180,
                    cells: { data: DateCell },
                },
                {
                    label: t('ra:updatedBy'),
                    field: '_updatedByString',
                    id: 'extra-data-field-updatedby',
                    minWidth: 180,
                },
                {
                    label: t('ra:updatedDate'),
                    field: 'updated',
                    id: 'extra-data-field-updated',
                    filter: 'date',
                    minWidth: 190,
                    cells: { data: DateCell },
                },
                {
                    field: 'recordId',
                    label: 'ID',
                    minWidth: 120,
                    id: 'extra-data-field-id',
                    filter: 'numeric',
                    showByDefault: false,
                },
            ]}
        />
    );
};

export default ExtraDataFieldsGrid;
